import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { getCCClosingSummary, getCCClosingDCADetail } from '../../api/AccountsAPI/ccClosingAPI';

/**
 * Read-only "CC Closing Summary" shown on the CC Closing verification page (closing type 5).
 * Legacy: Reports/CCClosingSummary partial — DCA consumed-budget grid (DCA rows expand into
 * Reports/GetDCABudgetDetailedSummary) + the "CC Closing Status" figures.
 * `onLoaded(ok)` fires once the summary request settles (legacy only shows Submit after it loads successfully).
 */
const dataOf = (res) => res?.data?.Data ?? res?.data ?? null;

const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return val ?? '';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const isNeg = (val) => parseFloat(val) < 0;

const StatusRow = ({ label, value, signed = false }) => (
    <div className="flex items-center justify-between gap-4 py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
        <span className="text-xs text-gray-700 dark:text-gray-300">{label}</span>
        <span className={`text-xs font-mono font-semibold whitespace-nowrap ${signed && isNeg(value) ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {fmt(value)}
        </span>
    </div>
);

const DETAIL_COLS = [
    ['Invoice Date', 'paydate'], ['Paid Date', 'PaidDate'], ['Description', 'Description'],
    ['Credit', 'credit'], ['Cash Debit', 'cashdebit'], ['Indent', 'indent'], ['PO', 'PO'],
    ['Invoice Debit', 'InvoiceDebit'], ['Internal Debit', 'InternalDebit'], ['Bank Debit', 'BankDebit'],
    ['Total Debit', 'TotalDebit'],
];
const TEXT_COLS = ['paydate', 'PaidDate', 'Description'];

const CCClosingSummaryView = ({ ccCode, roleName = '', onLoaded }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [expanded, setExpanded] = useState({});   // dcaCode → { loading, rows }

    useEffect(() => {
        if (!ccCode) return;
        let cancelled = false;
        setLoading(true);
        setSummary(null);
        setExpanded({});
        getCCClosingSummary(ccCode)
            .then((res) => {
                if (cancelled) return;
                const d = dataOf(res);
                setSummary(d);
                onLoaded?.(true);
            })
            .catch(() => { if (!cancelled) { setSummary(null); onLoaded?.(false); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccCode]);

    const toggleDCA = async (dcaCode) => {
        if (expanded[dcaCode]) {
            setExpanded(({ [dcaCode]: _, ...rest }) => rest);
            return;
        }
        setExpanded((p) => ({ ...p, [dcaCode]: { loading: true, rows: [] } }));
        try {
            const res = await getCCClosingDCADetail({ role: roleName, ccCode, dcaCode });
            const rows = dataOf(res);
            setExpanded((p) => ({ ...p, [dcaCode]: { loading: false, rows: Array.isArray(rows) ? rows : [] } }));
        } catch {
            setExpanded((p) => ({ ...p, [dcaCode]: { loading: false, rows: [] } }));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading CC closing summary…
            </div>
        );
    }

    const dcaRows = summary?.ConsolidateCCClosingData || [];
    const s = summary?.ConsolidateCCClosingStatusData?.[0];
    if (!summary || (!dcaRows.length && !s)) {
        return <div className="p-4 text-sm text-gray-500 text-center">No Data Found</div>;
    }

    return (
        <div className="space-y-4">
            {/* DCA consumed budget */}
            <div className="rounded-xl overflow-hidden border border-indigo-200 dark:border-indigo-700">
                <div className="bg-[#202447] text-white text-center text-sm font-semibold py-2">CC Closing Summary</div>
                {s && (
                    <div className="bg-[#202447] text-white grid grid-cols-2 md:grid-cols-4 gap-2 px-3 py-2 text-[11px] border-t border-white/30">
                        <span>CC Code :- {s.CCCode}</span>
                        <span className="truncate">CC Name :- {s.CCName}</span>
                        <span>Start Date :- {s.CCStartDate}</span>
                        <span>Closing Date :- {s.CCClosingDate}</span>
                    </div>
                )}
                <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-xs">
                        <thead className="bg-[#202447] text-white sticky top-0">
                            <tr>
                                <th className="w-8" />
                                <th className="px-3 py-2 text-center font-normal">DCA Code</th>
                                <th className="px-3 py-2 text-left font-normal">DCA Name</th>
                                <th className="px-3 py-2 text-right font-normal">Consumed Budget</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {dcaRows.map((r) => {
                                const canExpand = String(r.DCACode || '').substring(0, 3) === 'DCA';
                                const ex = expanded[r.DCACode];
                                return (
                                    <React.Fragment key={r.DCACode}>
                                        <tr className="bg-white dark:bg-gray-800">
                                            <td className="px-2 py-1.5 text-center">
                                                {canExpand && (
                                                    <button type="button" onClick={() => toggleDCA(r.DCACode)} className="text-indigo-600 dark:text-indigo-400">
                                                        {ex ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-center text-gray-800 dark:text-gray-200">{r.DCACode}</td>
                                            <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.DCAName}</td>
                                            <td className="px-3 py-1.5 text-right font-mono text-gray-800 dark:text-gray-200">{fmt(r.ConsumedBudget)}</td>
                                        </tr>
                                        {ex && (
                                            <tr>
                                                <td colSpan={4} className="bg-gray-50 dark:bg-gray-900/40 p-2">
                                                    {ex.loading ? (
                                                        <Loader2 className="w-4 h-4 animate-spin text-gray-400 mx-auto" />
                                                    ) : ex.rows.length === 0 ? (
                                                        <p className="text-center text-gray-400">No Data Found</p>
                                                    ) : (
                                                        <div className="overflow-x-auto">
                                                            <table className="w-full text-[11px]">
                                                                <thead className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                                                    <tr>{DETAIL_COLS.map(([h]) => <th key={h} className="px-2 py-1 font-normal text-center whitespace-normal">{h}</th>)}</tr>
                                                                </thead>
                                                                <tbody>
                                                                    {ex.rows.map((d, i) => (
                                                                        <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                                                                            {DETAIL_COLS.map(([h, k]) => (
                                                                                <td key={h} className={`px-2 py-1 text-gray-700 dark:text-gray-300 ${TEXT_COLS.includes(k) ? 'text-left' : 'text-right font-mono'}`}>
                                                                                    {TEXT_COLS.includes(k) ? (d[k] || '') : fmt(d[k])}
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CC Closing Status */}
            {s && (
                <div className="space-y-3">
                    <p className="text-center text-sm font-bold underline text-gray-900 dark:text-white">CC Closing Status</p>
                    <div className="rounded-xl border-2 border-gray-800 dark:border-gray-500 px-4 py-2 bg-white dark:bg-gray-800">
                        <StatusRow label="DEBIT NOTE ISSUED TO VENDOR FROM ACCOUNTS (NOT AFFECTED IN BUDGET CONSUMPTION)" value={s.DEBITNOTEISSUEDTOVENDOR} />
                        <StatusRow label="CREDIT NOTE ISSUED TO VENDOR FROM ACCOUNTS (NOT AFFECTED IN BUDGET CONSUMPTION)" value={s.CREDITNOTEISSUEDTOVENDOR} />
                        <StatusRow label="ACCOUNTED PAYABLE WRITTEN OFF MADE DURING CC/JOB CLOSURE" value={s.ACCOUNTEDPAYABLEWRITTENOFFMADEDURINGCCCLOSUER} />
                        <StatusRow label="PAYABLE WRITTEN OFF FINANCIAL YEAR" value={s.PAYABLEWRITTENOFFFINANCIALYEAR} />
                        <StatusRow label="TOTAL COST AFTER DEDUCTING WRITTEN OFF PAYABLES" value={s.TOTALCOSTAFTERDEDUCTINGWRITTENOFFPAYABLES} />
                    </div>
                    <div className="rounded-xl border-2 border-gray-800 dark:border-gray-500 px-4 py-2 bg-white dark:bg-gray-800">
                        <StatusRow label="CUMULATIVE INVOICE" value={s.CCCumulativeInvoice} />
                        <StatusRow label="CUMULATIVE CLIENT RECEIPT INCLUDING TDS" value={s.CUMULATIVECLIENTRECEIPTINCLUDINGTDS} />
                        <StatusRow label="CREDIT AGAINST NEW STOCK TRANSFER" value={s.CREDITAGAINSTNEWSTOCKTRANSFER} />
                        <StatusRow label="PROJECT STATUS WITH CUMULATIVE INVOICE" value={s.PROJECTSTATUSWITHCUMULATIVEINVOICE} signed />
                        <StatusRow label="PROJECT STATUS WITH CUMULATIVE CLIENT NET RECEIPT INCLUDING TDS" value={s.PROJECTSTATUSWITHCUMCLIENTNETRECEIPTINCTDS} signed />
                        <StatusRow label="PROJECT STATUS WITH CUMULATIVE CLIENT NET RECEIPT INCLUDING TDS & NEW STOCK TRANSFER CREDITS" value={s.PROJSTATWITHCUMCLIENTNETRECINCTDSNEWSTKTRANSCREDITS} signed />
                        <StatusRow label="DIFFERENCE IN CLIENT RECEIPT (CUMULATIVE BASIC NET RECEIPT INCLUDING TDS - CUMULATIVE BASIC INVOICE)" value={s.DIFFERENCEINCLIENTRECEIPT} signed />
                        <StatusRow label="CLIENT DEDUCTIONS AGAINST INVOICES" value={s.OtherDeductions} />
                        <StatusRow label="CLIENT DEDUCTIONS DONE THROUGH JV" value={s.JVDeductions} />
                        <StatusRow label="ADVANCE AMOUNT RECOVERED BY CLIENT" value={s.AdvanceRecovered} />
                        <StatusRow label="BAD DEBTS BOOKED" value={s.BADDEBTSBOOKED} />
                        <StatusRow label="BUDGET RECEIVED FROM OTHER CC TO MANAGE THE COST/LOSS" value={s.BUDGETRECIEVEDFROMOTHERCCTOMANAGETHECOSTorLOSS} />
                        <StatusRow label={`PERFORMING CC BUDGET TRANSFER TO CAPITAL AS NET MARGIN TRANSFER TO CAPITAL FOR FYYEAR ${s.CAPITALFYYEAR || ''}`} value={s.PCCBUDGETTRANSFERTOCAPITAL} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CCClosingSummaryView;
